<template>
  <div class="config-panel">
    <div class="panel-header">
      <div>
        <h3>Configure Source</h3>
        <p>Select a connection and endpoint</p>
      </div>
      <button @click="$emit('close')" class="btn-close">
        <X :size="20" />
      </button>
    </div>

    <div class="panel-body">
      <!-- Connection Selection -->
      <div class="form-group">
        <label>Connection</label>
        <select v-model="localData.connectionId" @change="onConnectionChange" class="form-select">
          <option :value="null">Select a connection...</option>
          <option v-for="conn in availableConnections" :key="conn.id" :value="conn.id">
            {{ conn.name }}
          </option>
        </select>
      </div>

      <!-- Endpoint Selection -->
      <div v-if="localData.connectionId" class="form-group">
        <label>Endpoint</label>
        <select v-model="localData.endpoint" @change="onEndpointChange" class="form-select">
          <option :value="null">Select an endpoint...</option>
          <option
            v-for="endpoint in availableEndpoints"
            :key="endpoint.path"
            :value="`${endpoint.method} ${endpoint.path}`"
          >
            {{ endpoint.method }} {{ endpoint.path }}
          </option>
        </select>
      </div>

      <!-- Connection Info -->
      <div v-if="selectedConnection" class="info-card">
        <div class="info-header">
          <Database :size="16" />
          <span>Connection Details</span>
        </div>
        <div class="info-content">
          <div class="info-item">
            <span class="label">Base URL:</span>
            <span class="value">{{ selectedConnection.baseUrl }}</span>
          </div>
          <div class="info-item">
            <span class="label">Auth Type:</span>
            <span class="value">{{ selectedConnection.authConfig?.type || "None" }}</span>
          </div>
        </div>
      </div>

      <!-- Loading indicator for sample data fetch -->
      <div v-if="isLoadingSample" class="loading-sample">
        <span class="loading-spinner" />
        <span>Fetching sample data…</span>
      </div>

      <!-- Sample Data Preview -->
      <div v-if="!isLoadingSample && sampleData.length > 0" class="sample-preview">
        <h4>Sample Data</h4>
        <div class="sample-table-container">
          <table class="sample-table">
            <thead>
              <tr>
                <th v-for="col in sampleColumns" :key="col">{{ col }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in sampleData.slice(0, 3)" :key="idx">
                <td v-for="col in sampleColumns" :key="col">
                  {{ formatValue(row[col]) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="panel-footer">
      <button @click="$emit('close')" class="btn btn-secondary">Cancel</button>
      <button @click="saveConfig" class="btn btn-primary" :disabled="!isValid">
        Apply Changes
      </button>
    </div>
  </div>
</template>

<script setup>
// @ts-nocheck - Will be properly typed later
import { ref, computed, watch } from "vue";
import { X, Database } from "lucide-vue-next";
import apiService from "@/Modules/ETL/services/api.js";
import { useNotification } from "@/Modules/ETL/composables/useNotification";
const props = defineProps({
  node: {
    type: Object,
    required: true,
  },
  availableConnections: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["update", "close"]);

const { success, error } = useNotification();

const localData = ref({
  connectionId: props.node.data.connectionId,
  endpoint: props.node.data.endpoint,
  label: props.node.data.label,
});

const sampleData = ref([]);
const availableEndpoints = ref([]);
const isLoadingSample = ref(false);

const selectedConnection = computed(() => {
  return props.availableConnections.find((c) => c.id === localData.value.connectionId);
});

const sampleColumns = computed(() => {
  if (sampleData.value.length === 0) return [];
  return Object.keys(sampleData.value[0]);
});

const isValid = computed(() => {
  return localData.value.connectionId && localData.value.endpoint;
});

watch(
  () => props.node.data,
  (newData) => {
    localData.value = {
      connectionId: newData.connectionId,
      endpoint: newData.endpoint,
      label: newData.label,
    };
  },
  { deep: true }
);

const onConnectionChange = () => {
  if (selectedConnection.value) {
    availableEndpoints.value = selectedConnection.value.selectedEndpoints || [];
    localData.value.endpoint = null;
    sampleData.value = [];
  }
};

// Cache the last successful sample fetch so saveConfig() can reuse it without a second round-trip
const sampleDataCache = ref(null); // { connectionId, endpoint, responseData }
// Monotonic counter used to discard results from superseded in-flight requests
let fetchSeq = 0;

const onEndpointChange = async () => {
  sampleDataCache.value = null;
  if (localData.value.endpoint && localData.value.connectionId) {
    const seq = ++fetchSeq;
    isLoadingSample.value = true;
    try {
      const response = await apiService.retrieveSampleDataFromEndpoint(
        localData.value.connectionId,
        localData.value.endpoint,
        3
      );

      // Discard stale result if the endpoint changed while we were waiting
      if (seq !== fetchSeq) return;

      // The backend returns { success, records, columns, fieldTypes }
      if (response.data.success && response.data.records) {
        // Convert JsonElement records to plain objects for display
        sampleData.value = response.data.records.map((record) => {
          const obj = {};
          response.data.columns.forEach((col) => {
            obj[col] = record[col];
          });
          return obj;
        });
        // Cache so saveConfig() does not need to re-fetch
        sampleDataCache.value = {
          connectionId: localData.value.connectionId,
          endpoint: localData.value.endpoint,
          responseData: response.data,
        };
      } else {
        sampleData.value = [];
      }
    } catch (error) {
      if (seq === fetchSeq) {
        console.error("Failed to fetch sample data:", error);
        sampleData.value = [];
      }
    } finally {
      if (seq === fetchSeq) {
        isLoadingSample.value = false;
      }
    }
  }
};

const formatValue = (value) => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const saveConfig = async () => {
  if (localData.value.connectionId && localData.value.endpoint) {
    try {
      // Reuse cached result from onEndpointChange to avoid a duplicate API call.
      // Only re-fetch if the cache is stale (different connection/endpoint).
      let responseData;
      const isCacheValid =
        sampleDataCache.value?.connectionId === localData.value.connectionId &&
        sampleDataCache.value?.endpoint === localData.value.endpoint;

      if (isCacheValid) {
        responseData = sampleDataCache.value.responseData;
      } else {
        const sampleResponse = await apiService.retrieveSampleDataFromEndpoint(
          localData.value.connectionId,
          localData.value.endpoint,
          3
        );
        responseData = sampleResponse.data;
      }

      if (!responseData.success) {
        // Check both errorMessage and message fields
        const errorMsg =
          responseData.errorMessage ||
          responseData.message ||
          "The external API encountered an error";
        console.error("Sample data fetch failed:", responseData);
        error("Failed to fetch sample data", errorMsg);
        return;
      }

      const columns =
        responseData.columns?.map((col) => ({
          name: col,
          type: responseData.fieldTypes?.[col] || "string",
        })) || [];

      emit("update", {
        connectionId: localData.value.connectionId,
        endpoint: localData.value.endpoint,
        label: `Source: ${selectedConnection.value?.name || "Data Source"}`,
        sampleColumns: columns,
      });

      success("Configuration saved", "Source configuration updated successfully");

      emit("close");
    } catch (err) {
      console.error("API call failed:", err);
      const errorMsg = err?.errorMessage || err?.message || "Failed to connect to the API";
      error("Connection failed", errorMsg);
    }
  } else {
    emit("update", {
      connectionId: localData.value.connectionId,
      endpoint: localData.value.endpoint,
      label: `Source: ${selectedConnection.value?.name || "Data Source"}`,
      sampleColumns: [],
    });
    emit("close");
  }
};
</script>

<style scoped>
.config-panel {
  position: fixed;
  top: 78px;
  right: 0;
  width: 450px;
  height: calc(100vh - 78px);
  background: white;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  z-index: 100;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.panel-header h3 {
  margin: 0 0 0.25rem 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.panel-header p {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-secondary);
}

.btn-close {
  background: transparent;
  border: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--color-text-primary);
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: all 0.2s;
}

.form-select:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.info-card {
  background: rgba(102, 126, 234, 0.05);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-accent);
  margin-bottom: 0.75rem;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
}

.info-item .label {
  color: var(--color-text-secondary);
  font-weight: 500;
}

.info-item .value {
  color: var(--color-text-primary);
  font-family: "Monaco", "Consolas", monospace;
  font-weight: 600;
}

.loading-sample {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--color-accent-light);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 8px;
  font-size: 13px;
  color: var(--color-accent);
  margin-bottom: 1rem;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(102, 126, 234, 0.3);
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.sample-preview {
  margin-top: 1.5rem;
}

.sample-preview h4 {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sample-table-container {
  overflow-x: auto;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
}

.sample-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.sample-table th {
  background: rgba(0, 0, 0, 0.02);
  padding: 0.75rem 0.5rem;
  text-align: left;
  font-weight: 600;
  color: var(--color-text-secondary);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.sample-table td {
  padding: 0.75rem 0.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  color: var(--color-text-primary);
}

.sample-table tr:last-child td {
  border-bottom: none;
}

.panel-footer {
  padding: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.02);
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary {
  background: transparent;
  color: var(--color-text-secondary);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.btn-secondary:hover {
  background: rgba(0, 0, 0, 0.05);
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
